/**
 * PageHeader — title, optional subtitle, optional Back, trailing actions.
 *
 * Back is a quiet icon-only control on the title row. Detail routes prefer
 * history when safe; primary nav pages can force a fixed parent path.
 */
import { el, type ElChild } from "../utils/dom";
import { navigateBack } from "../app/navigation";
import { renderInfoTooltip } from "./tooltip";

export interface PageHeaderBackOptions {
  /** Parent route used when history is not used, or as deep-link fallback. */
  fallbackPath: string;
  navigate: (path: string) => void;
  /** Accessible name; defaults to "Back". */
  label?: string;
  /**
   * When true (default), use history.back() when safe.
   * When false, always navigate to fallbackPath (e.g. main pages → Dashboard).
   */
  preferHistory?: boolean;
}

export interface PageHeaderOptions {
  title: string;
  /** Optional ⓘ help beside the page title. */
  titleHelp?: string;
  subtitle?: string;
  /** Tiny icon-only back control beside the title. */
  back?: PageHeaderBackOptions;
  actions?: ElChild[];
}

export function renderPageHeader(options: PageHeaderOptions): HTMLElement {
  const titleNode = options.titleHelp
    ? el("h1", { className: "page-header__title label-with-help" }, [
        options.title,
        renderInfoTooltip(options.titleHelp),
      ])
    : el("h1", { className: "page-header__title" }, [options.title]);

  const titleRow = el("div", { className: "page-header__title-row" }, [
    options.back ? renderBackControl(options.back) : null,
    titleNode,
  ]);

  const titles = el("div", { className: "page-header__titles" }, [
    titleRow,
    options.subtitle ? el("p", { className: "page-header__subtitle" }, [options.subtitle]) : null,
  ]);

  return el("div", { className: "page-header" }, [
    titles,
    options.actions?.length ? el("div", { className: "page-header__actions" }, options.actions) : null,
  ]);
}

function renderBackControl(options: PageHeaderBackOptions): HTMLElement {
  const label = options.label ?? "Back";
  return el(
    "button",
    {
      className: "page-header__back",
      type: "button",
      title: label,
      "aria-label": label,
      onclick: () => {
        if (options.preferHistory === false) {
          options.navigate(options.fallbackPath);
          return;
        }
        navigateBack(options.navigate, options.fallbackPath);
      },
    },
    [el("span", { className: "page-header__back-icon", "aria-hidden": "true" }, ["‹"])]
  );
}
